"use client";

import { AlertDialog } from "@base-ui/react/alert-dialog";
import { useTranslations } from "next-intl";
import { useSyncExternalStore } from "react";
import { Button } from "./Button";

type ConfirmationPrompt = {
  title: string;
  description?: string;
  acceptLabel?: string;
  declineLabel?: string;
  onAccept: () => void;
  onDecline?: () => void;
};

export function ConfirmationDialog() {
  const t = useTranslations("ConfirmationDialog");
  const prompt = useSyncExternalStore(subscribe, getPrompt, getPrompt);

  if (!prompt) {
    return null;
  }

  return (
    <AlertDialog.Root
      open
      onOpenChange={() => {
        close();
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Backdrop className="fixed inset-0 z-50 bg-black/30 transition-opacity" />
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <AlertDialog.Popup className="w-full max-w-150 rounded-xl border bg-surface p-6 shadow-lg">
            <AlertDialog.Title className="font-medium text-lg">
              {prompt.title}
            </AlertDialog.Title>
            {prompt.description && (
              <AlertDialog.Description className="mt-2 text-base text-muted">
                {prompt.description}
              </AlertDialog.Description>
            )}
            <div className="mt-4 flex flex-row justify-end gap-2">
              <AlertDialog.Close
                render={
                  <Button
                    variant="outline"
                    size="md"
                    onClick={() => {
                      prompt.onDecline?.();
                      close();
                    }}
                  />
                }
              >
                {prompt.declineLabel ?? t("declineButton")}
              </AlertDialog.Close>
              <AlertDialog.Close
                render={
                  <Button
                    variant="solid"
                    color="error"
                    size="md"
                    onClick={() => {
                      prompt.onAccept();
                      close();
                    }}
                  />
                }
              >
                {prompt.acceptLabel ?? t("acceptButton")}
              </AlertDialog.Close>
            </div>
          </AlertDialog.Popup>
        </div>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

let confirmationPrompt: ConfirmationPrompt | undefined;
let listeners: (() => void)[] = [];

export function confirmAction(prompt: ConfirmationPrompt) {
  confirmationPrompt = prompt;
  notify();
}

function subscribe(listener: (typeof listeners)[number]) {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function getPrompt() {
  return confirmationPrompt;
}

function notify() {
  for (const listener of listeners) {
    listener();
  }
}

function close() {
  confirmationPrompt = undefined;
  notify();
}
